import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { closeIcon } from "../utils/icons.js";
import { App } from "../model/app.js";
import { Api } from "../api.js";
import { Country, countryToCountryFilter } from "../model/country.js";
import { Platform } from "../model/platform.js";
import { FilterEvent, SearchEvent } from "../components/searchbar-component.js";
import { ActionEvent } from "../components/apps-list-component.js";
import { countryFilterToCountry } from "../model/country_filter.js";

export type ConnectAppEvent = CustomEvent<{
    appId: string;
    country: Country;
    platform: Platform;
}>;

@customElement("connect-app-modal")
export class ConnectAppModal extends LitElement {
    @state() private term: string = "";
    @state() private apps: App[] = [];

    @state() private loading: boolean = false;

    @property({ type: Object }) private country: Country = Country.UnitedStates;
    @property({ type: Object }) private platform: Platform = Platform.iPhone;

    createRenderRoot() {
        return this;
    }

    async fetchApps(query: string) {
        this.loading = true;
        const result = await Api.getApps(query, this.country, this.platform);
        if (result instanceof Error) {
            console.error("Error fetching apps", result.message);
        } else {
            this.apps = result;
        }
        this.loading = false;
    }

    handleSearch(event: SearchEvent) {
        this.term = event.detail.term;
        this.fetchApps(event.detail.term);
    }

    handleFilter(event: FilterEvent) {
        this.country = countryFilterToCountry(event.detail.countryFilter) ?? Country.UnitedStates;
        this.platform = event.detail.platform;
    }

    handleAppAction(event: ActionEvent) {
        const appId = event.detail.appId;
        this.dispatchEvent(new CustomEvent("connect-app", { detail: { appId, country: this.country, platform: this.platform } }));
        this.close();
    }

    close() {
        const closeButton = document.getElementById("close-button");
        if (closeButton) {
            closeButton.click();
        }
    }

    render() {
        return html`
            <dialog id="connect-app-modal" class="modal">
                <div class="modal-box w-2/3 max-w-5xl">
                    <div class="flex flex-col gap-4">
                        <form method="dialog">
                            <div class="flex flex-row items-center justify-between gap-4">
                                <div class="text-2xl font-bold">Connect App</div>
                                <button id="close-button" class="btn btn-circle btn-ghost">
                                    <div class="w-6 h-6">${closeIcon}</div>
                                </button>
                            </div>
                        </form>

                        <searchbar-component
                            .placeholder=${"Search for app or developer..."}
                            .countryFilter=${countryToCountryFilter(this.country)}
                            .platform=${this.platform}
                            ?loading=${this.loading}
                            ?showAllCountries=${false}
                            @search=${this.handleSearch}
                            @filter=${this.handleFilter}
                        ></searchbar-component>

                        <apps-list-component
                            .term=${this.term}
                            .action=${"Connect"}
                            .apps=${this.apps}
                            @action=${this.handleAppAction}
                        ></apps-list-component>
                    </div>
                </div>
                <form method="dialog" class="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        `;
    }

    renderApps() {
        return html`
            <div class="flex flex-col gap-4">
                ${this.apps.map(
                    (app) => html`
                        <div class="flex flex-row items-center justify-between gap-4">
                            <div class="text-2xl font-bold">${app.localization?.title}</div>
                        </div>
                    `
                )}
            </div>
        `;
    }
}
